import { ArrowRightIcon, CheckCircle2Icon, type LucideIcon } from 'lucide-react'
import { useMemo } from 'react'

import { Button } from 'slab'
import type { MoneyAmount } from 'types'
import type { MarketplacePurchase } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import { OrderDetails } from './OrderDetails'
import { Formatted } from '../product/Formatted'
import { SessionContext } from '../shared'

type MarketplacePurchaseConfirmationProps = {
  balance?: MoneyAmount | null;
  purchase: MarketplacePurchase;
  member_id?: string | null;
  employer_id?: string | null;
  is_authenticated: boolean;
  public_view: boolean;
}

export default function MarketplacePurchaseConfirmation ({
  balance,
  purchase,
  member_id: memberId,
  employer_id: employerId,
  is_authenticated: isAuthenticated,
}: MarketplacePurchaseConfirmationProps) {
  const { assign } = useWindowLocation()

  const sessionContext = useMemo(() => ({
    id: memberId ?? null,
    employerId: employerId ?? null,
    balance: balance ?? undefined,
    isAuthenticated,
    publicView: false,
    publicReferralUrl: '',
  }), [balance, employerId, isAuthenticated, memberId])

  const variant = purchase.variant

  const handleReturnToMarketplace = () => {
    assign('/members/marketplace')
  }

  return (
    <SessionContext.Provider value={sessionContext}>
      <div className='@container/confirmation flex fixed inset-0 left-0 md:left-60 overflow-x-auto overflow-y-hidden'>
        <main className='overflow-y-auto flex-1'>
          <div className='h-full px-8 lg:px-20 pt-20 lg:pt-10 pb-6 lg:pb-24'>
            <div className='flex flex-col gap-6 mt-2 @2xl/confirmation:mt-10 @2xl/confirmation:gap-8 max-w-[640px] w-full'>
              <div className='flex flex-col gap-2'>
                <h1 className='heading-lg'>We're processing your order! 🎉</h1>
                <p className='body-main text-gray-500'>
                  There are just a few more steps to get everything squared away
                </p>
              </div>

              <div className='flex flex-col gap-4'>
                <h2 className='heading-sm'>What happens next:</h2>

                <div className='flex flex-col gap-2'>
                  {variant.purchase_confirmation_text && (
                    <Notice icon={CheckCircle2Icon} iconClassName='text-green-400'>
                      <Formatted text={variant.purchase_confirmation_text} color='text-main' />
                    </Notice>
                  )}
                </div>
              </div>

              <div className='block @2xl/confirmation:hidden'>
                <OrderDetails
                  balance={balance ?? null}
                  purchase={purchase}
                  isMobileView
                />
              </div>

              <div className='flex justify-end mt-6 @2xl/confirmation:mt-10'>
                <Button
                  onClick={handleReturnToMarketplace}
                  size='large'
                  variant='primary'
                  rightIcon={ArrowRightIcon}
                >
                  Return to Marketplace
                </Button>
              </div>
            </div>
          </div>
        </main>

        <aside className='hidden @2xl/confirmation:block overflow-y-auto bg-gray-50 shadow-lg relative z-10 w-[360px] @4xl/confirmation:w-[480px] flex-shrink-0'>
          <OrderDetails
            balance={balance ?? null}
            purchase={purchase}
          />
        </aside>
      </div>
    </SessionContext.Provider>
  )
}

type NoticeProps = {
  children: React.ReactNode;
  icon: LucideIcon;
  iconClassName?: string;
}

function Notice ({ children, icon: Icon, iconClassName = 'text-gray-400' }: NoticeProps) {
  return (
    <div className='p-4 rounded-lg border border-gray-200 bg-white'>
      <div className='flex gap-2'>
        <div className='flex-shrink-0 mt-0.5'>
          <Icon className={`w-4 h-4 ${iconClassName}`} />
        </div>
        <p className='body-small text-gray-800'>
          {children}
        </p>
      </div>
    </div>
  )
}
